import React from 'react';
import PropTypes from 'prop-types';
import { Link as RouterLink } from 'react-router-dom';
import { TableCell, TableRow, Typography } from '@tempmee/react';
import { PromoCodeStatusLabel, PromoCodeTableCard, promoCodePath } from '../../PromoCodes';

// The ticket's columns. "Max Uses" is the per-office cap (0 when unlimited);
// the campaign-wide cap lives on the code's own page behind the Action link.
const COLUMNS = [
  'Code Name',
  'Discount',
  'Start Date',
  'End Date',
  'Code Status',
  'Applied',
  '# Used',
  'Max Uses',
  'Action',
];

// A node, not a string, for the reason BestInDentalCycleHistory gives:
// TableCard's default string title is 18px bold, which reads too heavy inside
// the profile tab.
const CARD_TITLE = (
  <Typography variant="subtitle2" as="h2" bold className="truncate text-text-primary">
    Discounts &amp; Promo Codes
  </Typography>
);

/**
 * One office's promo history (GOTU-6663): every code it holds or has redeemed
 * against, newest activity first, with whether and how often it was used. Uses
 * the shared `PromoCodeTableCard` frame — `outline` so the card stands out from
 * the tab's white background.
 *
 * "Applied" is the office-level flag the ticket asks for. Today every row the
 * service returns is applied — a code the office was merely eligible for and
 * never entered has no row in campaign-service to show — so a "No" here will
 * only appear once the service exposes per-office eligibility.
 */
export const OfficePromoCodesTable = ({ history, isPending, isError }) => (
  <PromoCodeTableCard
    title={CARD_TITLE}
    outline
    columns={COLUMNS}
    rowCount={history.length}
    isPending={isPending}
    isError={isError}
    emptyMessage="This office has not been on any promotion yet."
    errorMessage="Could not load discounts and promo codes."
  >
    {history.map((row) => (
      <TableRow key={row.campaignId} hover>
        <TableCell>{row.campaignName}</TableCell>
        <TableCell>{row.discountLabel}</TableCell>
        <TableCell>{row.startDateLabel}</TableCell>
        <TableCell>{row.endDateLabel}</TableCell>
        <TableCell>
          <PromoCodeStatusLabel status={row.status} label={row.statusLabel} />
        </TableCell>
        <TableCell>{row.appliedLabel}</TableCell>
        <TableCell>{row.usedCountLabel}</TableCell>
        <TableCell>{row.maxUsesLabel}</TableCell>
        <TableCell>
          <RouterLink to={promoCodePath(row.campaignId)}>View Code</RouterLink>
        </TableCell>
      </TableRow>
    ))}
  </PromoCodeTableCard>
);

OfficePromoCodesTable.propTypes = {
  history: PropTypes.arrayOf(
    PropTypes.shape({
      campaignId: PropTypes.string.isRequired,
      campaignName: PropTypes.string.isRequired,
      discountLabel: PropTypes.string.isRequired,
      startDateLabel: PropTypes.string.isRequired,
      endDateLabel: PropTypes.string.isRequired,
      status: PropTypes.string.isRequired,
      statusLabel: PropTypes.string.isRequired,
      appliedLabel: PropTypes.string.isRequired,
      usedCountLabel: PropTypes.string.isRequired,
      maxUsesLabel: PropTypes.string.isRequired,
    }),
  ).isRequired,
  isPending: PropTypes.bool.isRequired,
  isError: PropTypes.bool.isRequired,
};

export default OfficePromoCodesTable;
